// Pure URL <-> list-state <-> API-params mapping for server-paginated lists.
// No React, no `@/` imports: tested with node --experimental-strip-types.

export type SortOrder = 'asc' | 'desc';
export const PAGE_SIZES = [10, 20, 50, 100] as const;

export interface ListQuery {
  search: string;
  filters: Record<string, string>;
  sortBy: string;
  sortOrder: SortOrder;
  page: number;
  limit: number;
}

export interface ListQueryConfig {
  filterKeys: readonly string[];
  numericFilterKeys?: readonly string[];
  sortableColumns: readonly string[];
  defaultSortBy: string;
  defaultSortOrder: SortOrder;
  defaultLimit?: number;
}

const POSITIVE_INT = /^[1-9]\d*$/;
const FALLBACK_LIMIT = 20;

function isPageSize(n: number): boolean {
  return (PAGE_SIZES as readonly number[]).includes(n);
}

function defaultLimitOf(c: ListQueryConfig): number {
  return c.defaultLimit !== undefined && isPageSize(c.defaultLimit) ? c.defaultLimit : FALLBACK_LIMIT;
}

export function defaultListQuery(c: ListQueryConfig): ListQuery {
  return {
    search: '',
    filters: {},
    sortBy: c.defaultSortBy,
    sortOrder: c.defaultSortOrder,
    page: 1,
    limit: defaultLimitOf(c),
  };
}

export function parseListQuery(params: URLSearchParams, c: ListQueryConfig): ListQuery {
  const q = defaultListQuery(c);
  q.search = (params.get('q') ?? '').trim();

  const page = params.get('page');
  if (page !== null && POSITIVE_INT.test(page)) {
    const n = Number(page);
    if (Number.isSafeInteger(n)) q.page = n;
  }

  const limit = params.get('limit');
  if (limit !== null && POSITIVE_INT.test(limit) && isPageSize(Number(limit))) q.limit = Number(limit);

  const sort = params.get('sort');
  if (sort !== null && c.sortableColumns.includes(sort)) q.sortBy = sort;

  const order = params.get('order');
  if (order === 'asc' || order === 'desc') q.sortOrder = order;

  // Iterate the allow-list, never the incoming params, so hostile keys cannot be assigned.
  for (const key of c.filterKeys) {
    const raw = params.get(key);
    if (raw === null) continue;
    const value = raw.trim();
    if (value === '') continue;
    if (c.numericFilterKeys?.includes(key) && !POSITIVE_INT.test(value)) continue;
    q.filters[key] = value;
  }
  return q;
}

export function serializeListQuery(q: ListQuery, c: ListQueryConfig): URLSearchParams {
  const d = defaultListQuery(c);
  const out = new URLSearchParams();
  if (q.search !== '') out.set('q', q.search);
  for (const key of c.filterKeys) {
    const v = Object.hasOwn(q.filters, key) ? q.filters[key] : undefined;
    if (v !== undefined && v !== '') out.set(key, v);
  }
  if (q.sortBy !== d.sortBy) out.set('sort', q.sortBy);
  if (q.sortOrder !== d.sortOrder) out.set('order', q.sortOrder);
  if (q.page !== 1) out.set('page', String(q.page));
  if (q.limit !== d.limit) out.set('limit', String(q.limit));
  return out;
}

/**
 * Replaces exactly the list-owned keys (q, page, limit, sort, order and `c.filterKeys`) of `existing`
 * with those in `next`; every unrelated param is kept in its original position. Inputs are not mutated.
 */
export function mergeListParams(existing: URLSearchParams, next: URLSearchParams, c: ListQueryConfig): URLSearchParams {
  const owned = new Set<string>(['q', 'page', 'limit', 'sort', 'order', ...c.filterKeys]);
  const out = new URLSearchParams();
  for (const [k, v] of existing) if (!owned.has(k)) out.append(k, v);
  for (const [k, v] of next) out.append(k, v);
  return out;
}

export function toApiParams(q: ListQuery): Record<string, string | number> {
  return {
    ...(q.search ? { search: q.search } : {}),
    ...q.filters,
    limit: q.limit,
    offset: (q.page - 1) * q.limit,
    sort_by: q.sortBy,
    sort_order: q.sortOrder,
  };
}

export function withFilter(q: ListQuery, key: string, value: string | undefined): ListQuery {
  const filters = { ...q.filters };
  if (value === undefined || value === '') delete filters[key];
  else filters[key] = value;
  return { ...q, filters, page: 1 };
}

export function withSearch(q: ListQuery, search: string): ListQuery {
  return { ...q, search, page: 1 };
}

export function withSort(q: ListQuery, column: string, c: ListQueryConfig): ListQuery {
  if (!c.sortableColumns.includes(column)) return q;
  const sortOrder: SortOrder = q.sortBy === column ? (q.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
  return { ...q, sortBy: column, sortOrder, page: 1 };
}

export function pageCount(total: number, limit: number): number {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(limit) || limit <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

export function withPage(q: ListQuery, page: number, total: number): ListQuery {
  if (!Number.isFinite(page)) return q;
  const next = Math.min(Math.max(1, Math.floor(page)), pageCount(total, q.limit));
  return next === q.page ? q : { ...q, page: next };
}

export function hasActiveFilters(q: ListQuery): boolean {
  return q.search.trim() !== '' || Object.keys(q.filters).length > 0;
}

/** Drops search and filters (keeps sort and page size). Returns the same reference when nothing is active. */
export function clearFilters(q: ListQuery): ListQuery {
  if (q.search.trim() === '' && Object.keys(q.filters).length === 0) return q;
  return { ...q, search: '', filters: {}, page: 1 };
}

export function withLimit(q: ListQuery, limit: number): ListQuery {
  if (!isPageSize(limit) || limit === q.limit) return q;
  return { ...q, limit, page: 1 };
}

/**
 * Pagination slots (size >= 7, default 7). Never more than `size` entries.
 * - pages <= size: every page, no gaps.
 * - Otherwise first and last page are always shown, and a gap always hides >= 2 pages:
 *   - page <= size-3 (near start): 1..size-2, gap, last
 *   - page >= pages-(size-4) (near end): 1, gap, pages-(size-3)..pages
 *   - else (middle): 1, gap, a centered window of size-4 pages, gap, last
 * `page` is clamped to 1..pages. For size 7: [1,2,3,4,5,gap,N], [1,gap,p-1,p,p+1,gap,N], [1,gap,N-4..N].
 */
export function pageWindow(page: number, pages: number, size = 7): (number | 'gap')[] {
  const total = Number.isFinite(pages) ? Math.max(1, Math.floor(pages)) : 1;
  const slots = Math.max(7, Math.floor(size) || 7);
  const cur = Number.isFinite(page) ? Math.min(Math.max(1, Math.floor(page)), total) : 1;
  const range = (a: number, b: number): number[] => Array.from({ length: b - a + 1 }, (_, i) => a + i);

  if (total <= slots) return range(1, total);
  if (cur <= slots - 3) return [...range(1, slots - 2), 'gap', total];
  if (cur >= total - (slots - 4)) return [1, 'gap', ...range(total - (slots - 3), total)];
  const w = slots - 4;
  const lo = cur - Math.floor(w / 2);
  return [1, 'gap', ...range(lo, lo + w - 1), 'gap', total];
}
